import React from "react";
import moment from "moment";
import {
    Card,
    Grid,
    Box,
    Stack,
    Avatar,
    Chip,
    Tooltip,
    Typography
} from "@mui/material";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { formatTokenAmount, transformToBTC } from "../../../utils/helpers";

const POSITIVE = [
    "Deposit",
    "Free BTC Bonus",
    "Referral",
    "Referral Rewards",
    "Reward Claim",
    "Voucher Claim",
    "Bonding Reward",
    "Add Liquidity"
];
const NEGATIVE = [
    "Withdraw",
    "Withdraw failed",
    "Failed Withdraw Refund",
    "Remove Liquidity",
    "Mint Fee",
    "Transfer Fee"
];

const getChipColor = (desc) => {
    if (POSITIVE.includes(desc)) return "success";
    if (NEGATIVE.includes(desc)) return "error";
    return "default";
};

// Function to get the display description for trades
const getDisplayDescription = (transfer) => {
    const originalDescription = transfer.description;

    if (originalDescription === "Trade") {
        const type = transfer.type
        if (type === "add") return "Buy";
        if (type === "sub") return "Sell";
    }

    if (originalDescription === "Transfer") {
        const type = transfer.type
        if (type === "add") return "Receive";
        if (type === "sub") return "Send";
    }

    return originalDescription;
};

// Function to get chip color for trades
const getTradeChipColor = (transfer) => {
    const displayDescription = getDisplayDescription(transfer);

    if (displayDescription === "Buy") return "success";
    if (displayDescription === "Sell") return "error";

    return getChipColor(displayDescription);
};
// --- Utility Function ---
const shortenId = (id, startLength = 3, endLength = 3) => {
    if (!id || id.length <= startLength + endLength) return id;
    return `${ id.substring(0, startLength) }...${ id.substring(id.length - endLength) }`;
};
const TimeAgo = ({ date }) => (
    <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, lineHeight: 1 }}>
        {moment(date).fromNow()} {/* 👈 using moment */}
    </Typography>
);

const TokenData = ({ transfer }) => {
    // if (!transfer.token) return null;

    const tokenName = transfer.token?.name || "btc";
    const tokenIdForDisplay = transfer.token?.id || "";
    const shortName =
        tokenName.length > 12 ? tokenName.slice(0, 12) + "…" : tokenName;

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, overflow: 'hidden' }}>
            <Typography
                component="span"
                sx={{
                    fontFamily: "monospace",
                    fontSize: ".8rem",
                    fontWeight: "bold",
                    color: "text.secondary",
                    flexShrink: 0
                }}
            >
                {
                    shortName?.toLowerCase() !== "btc" ? (
                        formatTokenAmount(transfer.amount)
                    ) : (
                        transformToBTC(transfer.amount)
                    )
                }
            </Typography>
            {
                shortName?.toLowerCase() !== "btc" && (
                    <Avatar
                        alt={tokenName}
                        src={`https://images.odin.fun/token/${ tokenIdForDisplay }`}
                        sx={{ width: 14, height: 14, flexShrink: 0 }}
                    />
                )
            }
            <Typography
                variant="caption"
                component="span"
                sx={{
                    fontSize: ".8rem",
                    fontWeight: "bold",
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0
                }}
            >
                {shortName !== "btc" && shortName}
            </Typography>


        </Box>
    );
};


const PillLabel = ({ children }) => (
    <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: "0.62rem", fontWeight: 700, lineHeight: 1 }}
    >
        {children}
    </Typography>
);

const Mono = ({ children, sx }) => (
    <Typography
        component="span"
        sx={{
            fontFamily: "monospace",
            fontSize: "0.72rem",
            fontWeight: 700,
            lineHeight: 1,
            ...sx
        }}
    >
        {children}
    </Typography>
);

// expects shortenId(id, left=3, right=3) to exist
export default function TransferItem({ transfer }) {
    const description =
        transfer?.description 
    const displayDescription = getDisplayDescription(transfer);
    const hasBoth =false

    return (
        <Card
            sx={{
                px: 1,
                py: 0.75,
                borderRadius: 2,
                transition: "box-shadow .2s ease",
                "&:hover": { boxShadow: 6 },
                minHeight: "80px"
            }}
        >
            <Grid container alignItems="center" spacing={1} sx={{ rowGap: 0 }}>
                <Grid item xs={12}>
                    <TokenData transfer={transfer} />
                </Grid>
                {/* Left: Token + Description */}
                <Grid item xs={12} sm={6} md={5} lg={4}>
                    <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap" sx={{ minWidth: 0 }}>

                        {description && (
                            <Chip
                                label={displayDescription}
                                color={getTradeChipColor(transfer)}
                                size="small"
                                sx={{
                                    height: 20,
                                    flexShrink: 0,
                                    "& .MuiChip-label": {
                                        px: 0.75,
                                        fontSize: "0.62rem",
                                        fontWeight: 700,
                                        lineHeight: 1
                                    }
                                }}
                            />
                        )}
                    </Stack>
                </Grid>

                {/* Middle: Sender → Receiver or Single User */}
                <Grid item xs={12} sm={4} md={5} lg={6}>
                    {hasBoth ? (
                        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                            <PillLabel>Route</PillLabel>
                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, overflow: 'hidden' }}>
                               
                                <Mono sx={{ flexShrink: 0 }}>{shortenId(transfer.user, 3, 3)}</Mono>
                            </Stack>
                        </Stack>
                    ) : (
                        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                            <PillLabel>User</PillLabel>
                            <Mono>{shortenId(transfer.user, 3, 3)}</Mono>
                        </Stack>
                    )}
                </Grid>

                {/* Right: Time (right-aligned) */}
                <Grid item xs={12} sm={2} md={2} lg={2}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "flex-start", sm: "flex-end" } }}>
                        <PillLabel>Time</PillLabel>
                        <TimeAgo date={transfer.time} />
                    </Box>
                </Grid>
            </Grid>
        </Card>
    );
}
